import {
  CHECK_ID_STATUS,
  validateCodigoPostal,
  validateFechaNacimiento,
  validateNombre,
  validateRegimenFiscal,
  validateRfcCurpConsistency,
} from "@/lib/checkIdLocalValidation";

function fieldOk(validationField) {
  if (!validationField?.provided) return null;
  return validationField.valid_format === true;
}

function fieldMessage(validationField, fallback = "Sin datos") {
  if (!validationField?.provided) return "No capturado";
  return validationField.message || fallback;
}

function integrationCheck(id, label, block) {
  const status = (block?.status || "").toLowerCase();
  const message = block?.message || block?.status || "Sin datos";
  if (status === "not_connected" || status === "sin_consulta" || status === "pendiente") {
    return { id, label, ok: null, message, pendingIntegration: true };
  }
  if (status === "invalido" || status === "error") {
    return { id, label, ok: false, message };
  }
  if (status === "offline_estructural") {
    return { id, label, ok: true, message: message || "Validación estructural local" };
  }
  return { id, label, ok: true, message };
}

/**
 * Combina respuesta POST /identity/check con validaciones locales opcionales del formulario.
 */
export function mapIdentityApiToCheckIdResult(apiResponse, localFields = {}) {
  const validation = apiResponse?.validation || {};
  const checks = [
    { id: "nombre", label: "Nombre", ...validateNombre(localFields.nombre) },
    {
      id: "rfc",
      label: "RFC",
      ok: fieldOk(validation.rfc),
      message: fieldMessage(validation.rfc, "RFC no capturado"),
    },
    {
      id: "curp",
      label: "CURP",
      ok: fieldOk(validation.curp),
      message: fieldMessage(validation.curp, "CURP no capturada"),
    },
    {
      id: "nss",
      label: "NSS / Número IMSS",
      ok: fieldOk(validation.nss),
      message: fieldMessage(validation.nss, "NSS no capturado"),
    },
    {
      id: "rfc_curp",
      label: "Consistencia RFC–CURP",
      ...validateRfcCurpConsistency(localFields.rfc, localFields.curp),
    },
    {
      id: "fecha",
      label: "Fecha de nacimiento",
      ...validateFechaNacimiento(localFields.fechaNacimiento),
    },
    {
      id: "cp",
      label: "C.P. fiscal",
      ...validateCodigoPostal(localFields.codigoPostal),
    },
    {
      id: "regimen",
      label: "Régimen fiscal",
      ...validateRegimenFiscal(localFields.regimenFiscal),
    },
    integrationCheck("sat", "Consulta SAT", apiResponse?.sat),
    integrationCheck("art69", "Lista Art. 69 / EFOS", apiResponse?.art69),
    integrationCheck("imss", "Validación IMSS", apiResponse?.imss),
    integrationCheck("renapo", "Consulta RENAPO (CURP)", apiResponse?.curp_data),
  ];

  const requiredEmpty = ["nombre", "rfc", "curp"].some(
    (key) => !String(localFields[key] || "").trim(),
  );
  const hasFailure = checks.some((c) => c.ok === false);
  const optionalPending = checks.some(
    (c) =>
      !["nombre", "rfc", "curp", "rfc_curp", "sat", "art69", "imss", "renapo"].includes(c.id)
      && c.ok === null,
  );
  const consistencyPending = checks.find((c) => c.id === "rfc_curp")?.ok === null;
  const apiFailed = apiResponse?.ok === false || apiResponse?.success === false;

  let status = CHECK_ID_STATUS.VALID;
  let summary = "Datos listos para continuar con el expediente.";

  if (requiredEmpty) {
    status = CHECK_ID_STATUS.INCOMPLETE;
    summary = "Faltan datos obligatorios (nombre, RFC y CURP).";
  } else if (hasFailure || apiFailed) {
    status = CHECK_ID_STATUS.INCONSISTENT;
    summary = "Hay inconsistencias o formatos inválidos. Corrija antes de inscribir.";
  } else if (optionalPending || consistencyPending) {
    status = CHECK_ID_STATUS.PENDING;
    summary = "Datos básicos correctos; complete NSS, régimen o C.P. para un expediente más sólido.";
  }

  if (apiResponse?.offline) {
    summary = `${summary} Modo sin conexión: solo validación estructural.`;
  }

  return {
    status,
    checks,
    summary,
    api: apiResponse,
    warnings: apiResponse?.warnings || [],
  };
}

export function resolveCheckIdSourceModule(variant) {
  if (variant === "tramites" || variant === "tramites-nss") return "tramites";
  if (variant === "laboral") return "datos_maestros";
  return "manual";
}
